import type FindItemResponseMessage from 'owa-service/lib/contract/FindItemResponseMessage';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import indexedPageView from 'owa-service/lib/factory/indexedPageView';
import type SortResults from 'owa-service/lib/contract/SortResults';
import type ViewFilter from 'owa-service/lib/contract/ViewFilter';
import { findItem } from './findItem';
import { tryConsumeFindMailFolderItemResponse } from 'mini-session-storage/lib/utils/sessionDataResponseStash';

interface FindItemWithStartIndexParams {
    folderId: string;
    rowsToLoad: number;
    viewFilter: ViewFilter;
    focusedViewFilter: FocusedViewFilter;
    shouldTryToUseSessionDataResponse: boolean;
    sortOrder: SortResults[];
}
/**
 * The FindItemWithStartIndex is used today to load rows when we want to load them from the beginning (or 0th index)
 * This is currently used in load initial rows and in reload scenarios
 * @param folderId the folder id
 * @param rowsToLoad number of rows to load
 * @param focusedViewFilter focused/other filter with which to find the items
 * @param shouldTryToUseSessionDataResponse Indicates if we should try to use the result returned in session data response
 * @returns a promise of type FindItemResponseMessage
 */
export default function findItemWithStartIndex({
    folderId,
    rowsToLoad,
    viewFilter,
    focusedViewFilter,
    shouldTryToUseSessionDataResponse,
    sortOrder,
}: FindItemWithStartIndexParams): Promise<FindItemResponseMessage> {
    if (shouldTryToUseSessionDataResponse) {
        const sessionDataFindMailFolderItemResponse = tryConsumeFindMailFolderItemResponse(
            folderId,
            0 /* startIndex */,
            folderNameToId('inbox')
        );

        if (sessionDataFindMailFolderItemResponse) {
            // Return the session data response if available instead of going to the server
            return Promise.resolve(sessionDataFindMailFolderItemResponse);
        }
    }

    const paging = indexedPageView({
        BasePoint: 'Beginning',
        Offset: 0 /* startIndex */,
        MaxEntriesReturned: rowsToLoad,
    });

    return findItem({
        currentFolderId: folderId,
        paging,
        sortOrder,
        focusedViewFilter,
        viewFilter,
    });
}
